import { useRef, useEffect } from 'react';

/**
 * Lightweight render counter for spotting components that re-render too often.
 *
 * It is a NO-OP unless you explicitly enable it, so it is safe to leave wired
 * into production components. Enable it either way:
 *   • add `?debug=render` to the URL, or
 *   • run `window.__RENDER_DEBUG__ = true` in the console, then interact.
 *
 * It logs on every commit: the component name, how many times it has rendered,
 * and how long since its previous render (a burst of tiny gaps = a render loop).
 * Pass `props` (any object) to see which value changed between renders.
 */
export default function useRenderLog(name, props) {
    const count = useRef(0);
    const lastTime = useRef(0);
    const prevProps = useRef(props);

    count.current += 1;

    useEffect(() => {
        const now = (typeof performance !== 'undefined' ? performance.now() : 0);
        const sinceLast = lastTime.current ? Math.round(now - lastTime.current) : 0;
        lastTime.current = now;

        // Which props changed since last render (shallow).
        let changed;
        if (props && prevProps.current) {
            changed = Object.keys(props).filter((k) => props[k] !== prevProps.current[k]);
        }
        prevProps.current = props;

        // eslint-disable-next-line no-console
        console.log(
            `%c[render] ${name} #${count.current}%c (+${sinceLast}ms)`,
            'color:#8B5CF6;font-weight:bold',
            'color:#888',
            changed && changed.length ? { changedProps: changed } : ''
        );
    });
}
